<script setup lang="ts">
import type { PathwayEvent, Step } from '@/types/shared/pathway-event'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import PathwayEventStep from '@/components/PathwayEventStep.vue'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { computed, ref } from 'vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import { useConfig } from '@/composables/game/config'
import { formatUnshortenedNumber } from '@/helpers/utils'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
	show: boolean
	step: Step & { index: number, originalIndex: number }
	pathwayEvent: PathwayEvent
}>()
const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { config } = useConfig()
const maybeEcommerceItem = computed(() => config.value.game.ecommerce.items.find(item => item.id === `${SPECIAL_ECOMMERCE_ITEM_ID.PATHWAY_STEP}.${props.pathwayEvent.id}`))
const ecommerceItem = computed(() => {
  if (!maybeEcommerceItem.value) throw new Error('Ecommerce item not found with the id: ' + `${SPECIAL_ECOMMERCE_ITEM_ID.PATHWAY_STEP}.${props.pathwayEvent.id}`)
  return maybeEcommerceItem.value
})

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(ecommerceItem.value)

const isLoading = ref(false)
const purchased = ref(false)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      purchased.value = true
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}

function onClose() {
  if (purchased.value) {
    emit('purchaseDone')
  }
  emit('hide')
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    bg-fade-class="z-[802]"
    wrapper-class="z-[802]"
    content-class="pt-6"
    @hide="onClose"
  >
    <div
      class="w-full relative"
      :class="step.index === 0 ? 'pt-[60px]' : 'pt-6'"
    >
      <StarsBalancePill
        class="absolute left-0 top-0"
      />
      <PathwayEventStep
        class="mx-auto max-w-fit"
        :event-id="pathwayEvent.id"
        inner-container-class="px-[29px] pt-0 pb-[6px] justify-center"
        :main-color="pathwayEvent.mainColor"
        :step="step"
        only-visual
        :has-arrow="false"
        is-claimable
        :multi-reward-support="step.index === 0"
      />
      <Transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="purchased"
          class="mt-3 w-full flex flex-col items-center"
        >
          <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            Purchase successful!
          </div>
          <div class="text-center text-white/[.60] text-sm font-medium">
            {{ pathwayEvent.eventName }} Step #{{ step.originalIndex }}
          </div>
          <BaseButton
            class="mt-12 w-full"
            @click="onClose"
          >
            Close
          </BaseButton>
        </div>
        <div
          v-else
          class="mt-6 w-full flex flex-col items-center"
        >
          <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            {{ pathwayEvent.eventName }} Step #{{ step.originalIndex }} = ${{ starPrice.usdAmount.toFixed(2) }}
          </div>

          <div class="mt-12 w-full flex flex-col gap-y-3">
            <BaseButton
              class="w-full"
              :loading="isBuyingWithStars"
              @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
            >
              <span
                v-if="!isBuyingWithStars"
                class="flex items-center gap-x-2"
              >
                <T
                  key-name="starter_pack.buy_with"
                  default-value="Buy with"
                />
                <ThePremium :with-tooltip="false" />
                <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
              </span>
              <T
                v-else
                key-name="lingo.giveaway.transaction.loading_cta"
                default-value="Processing..."
              />
            </BaseButton>

            <template v-if="showTonPurchase">
              <div class="relative w-full">
                <div class="absolute right-12 -top-1 rotate-[20deg]">
                  <TonDiscountBadge label="-10%" />
                </div>
                <BaseButton
                  style-type="tertiary"
                  :loading="isBuyingWithTon"
                  class="w-full"
                  @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
                >
                  <span
                    v-if="!isBuyingWithTon"
                    class="flex items-center gap-x-2"
                  >
                    <T
                      key-name="starter_pack.buy_with"
                      default-value="Buy with"
                    />
                    <img
                      src="@/assets/images/ton-symbol.png"
                      class="size-5 min-w-[20px] object-cover"
                    >
                    <span>{{ tonPrice?.amount }}</span>
                  </span>
                  <T
                    v-else
                    key-name="lingo.giveaway.transaction.loading_cta"
                    default-value="Processing..."
                  />
                </BaseButton>
              </div>

              <div
                class="mx-auto -mt-1 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium"
              >
                <T
                  key-name="transaction.process_description"
                  default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
                />
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>